/**
 * The far end of a home dispatch. The home navigated the moment the canvas
 * existed; the session and the first message were still in flight. This
 * claims that work and opens the running session in the floating chat. When
 * the message never left, the prompt lands in the chat's composer instead,
 * so nothing typed on the home is lost. A quick action's header goes on the
 * canvas first, at the origin, and the dispatcher holds the message until it
 * is there. Ordinary canvas visits have no pending work and this renders
 * nothing.
 */

import { pushComposerDraft } from "@/composer/composerDraft";
import Sentry from "@/integrations/Sentry";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { useCanvasApi } from "../context/CanvasContext";
import { useCanvasFloatingChat } from "../context/CanvasFloatingChatContext";
import { useCanvasRoute } from "../context/CanvasRouteContext";
import {
  useClaimedDispatch,
  type PendingDispatch,
} from "../hooks/canvasDispatch";
import { useEnsureCanvasAgentChatOpen } from "../hooks/useEnsureCanvasAgentChatOpen";
import { placeHtmlBlock } from "../utils/placeHtmlBlock";

export const AGENT_FAILED =
  "The agent didn't start. Your message is in the chat, ready to send.";

export function CanvasDispatchArrival() {
  const { canvasId } = useCanvasRoute();
  const pending = useClaimedDispatch(canvasId);
  const { openSession } = useCanvasFloatingChat();
  const ensureChatOpen = useEnsureCanvasAgentChatOpen();
  const queryClient = useQueryClient();
  const canvas = useCanvasApi();
  const handled = useRef<PendingDispatch | null>(null);
  const placed = useRef<PendingDispatch | null>(null);

  useEffect(() => {
    const header = pending?.header;
    // The store drops a node added before the room has loaded.
    if (!pending || !header || !canvasId || !canvas.isReady) return;
    if (placed.current === pending) return;
    placed.current = pending;
    void placeHtmlBlock({
      canvasId,
      queryClient,
      canvas,
      file: header.file,
      html: header.html,
      position: { x: 0, y: 0 },
      width: header.width,
      height: header.height,
      chromeless: true,
      source: "home_header",
      // Refused once the message went without it: the agent makes its own.
      admit: () => header.settle(true),
    }).catch((error) => {
      Sentry.captureException(error);
      header.settle(false);
    });
  }, [pending, canvasId, canvas, queryClient]);

  useEffect(() => {
    if (!pending || handled.current === pending) return;
    let live = true;
    void pending.outcome.then((outcome) => {
      if (!live || handled.current === pending) return;
      // Consume before opening the chat: opening it changes ensureChatOpen's
      // identity and reruns this effect, and a settled promise must not
      // replace the session stack a second time.
      handled.current = pending;
      if (outcome.kind === "sent") {
        openSession(outcome.sessionId, "replace");
        return;
      }
      toast.error(AGENT_FAILED);
      if (outcome.kind === "post-failed") {
        pushComposerDraft(queryClient, outcome.sessionId, pending.prompt);
        openSession(outcome.sessionId, "replace");
      } else {
        void ensureChatOpen(pending.prompt);
      }
    });
    return () => {
      live = false;
    };
  }, [pending, openSession, ensureChatOpen, queryClient]);

  return null;
}
